import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import HomePage from './pages/homepage';
import AboutOrazekPage from './pages/about-orazek';
import ServicesOverviewPage from './pages/services-overview';
import RealEstatePortalPage from './pages/real-estate-portal';
import VehicleRentalPage from './pages/vehicle-rental';
import ContactPage from './pages/contact';
import AdminLogin from './pages/admin-login';
import ProtectedAdminDashboard from './pages/protected-admin-dashboard';
import InventoryManagementDashboard from './pages/inventory-management-dashboard';
import TeamPlanningScheduling from './pages/team-planning-scheduling';
import NotificationManagementCenter from './pages/notification-management-center';
import LiveNotificationCenter from './pages/live-notification-center';
import ClientPortalDashboard from './pages/client-portal-dashboard';
import ProjectStatusTracker from './pages/project-status-tracker';
import CommunicationCenter from './pages/communication-center';
import NotFound from './pages/NotFound';
import AdminCRUDManagement from './pages/admin-crud-management';
import NewsArticlesHub from './pages/news-articles-hub';
import ArticleDetailPage from './pages/news-articles-hub/ArticleDetailPage';
import ForgotPassword from './pages/forgot-password';
import UpdatePassword from './pages/update-password';
import PrivacyPolicy from './pages/privacy-policy';
import TermsOfService from './pages/terms-of-service';
import LegalNotice from './pages/legal-notice';
import QuoteRequestsManagement from './pages/quote-requests-management';
import UserManagement from './pages/user-management';

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <RouterRoutes location={location} key={location?.pathname}>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about-orazek" element={<AboutOrazekPage />} />
        <Route path="/services-overview" element={<ServicesOverviewPage />} />
        <Route path="/real-estate-portal" element={<RealEstatePortalPage />} />
        <Route path="/vehicle-rental" element={<VehicleRentalPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/news-articles-hub" element={<NewsArticlesHub />} />
        <Route path="/news-articles-hub/:id" element={<ArticleDetailPage />} />

        {/* Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/legal-notice" element={<LegalNotice />} />

        {/* Admin Authentication */}
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Protected Admin Routes */}
        <Route
          path="/protected-admin-dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProtectedAdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inventory-management-dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'staff']}>
              <InventoryManagementDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-planning-scheduling"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager']}>
              <TeamPlanningScheduling />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notification-management-center"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager']}>
              <NotificationManagementCenter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-notification-center"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'staff']}>
              <LiveNotificationCenter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-portal-dashboard"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'staff']}>
              <ClientPortalDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-status-tracker"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'staff']}>
              <ProjectStatusTracker />
            </ProtectedRoute>
          }
        />
        <Route
          path="/communication-center"
          element={
            <ProtectedRoute allowedRoles={['admin', 'manager', 'staff']}>
              <CommunicationCenter />
            </ProtectedRoute>
          }
        />
        <Route path="/admin-crud-management" element={<ProtectedRoute allowedRoles={['admin']}><AdminCRUDManagement /></ProtectedRoute>} />
        <Route path="/quote-requests-management" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><QuoteRequestsManagement /></ProtectedRoute>} />
        <Route path="/user-management" element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </AnimatePresence>
  );
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AuthProvider>
          <AnimatedRoutes />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
